import React, { useEffect, useState, useCallback } from 'react';
import { parametersAPI } from '../../api';
import { Loading, Modal, Pagination } from '../../components';
import { usePagination } from '../../hooks';
import { useToast } from '../../context/ToastContext';
import './Parameters.css';

// Parameter categories - ORGANOLEPTIC_PHYSICAL combines both organoleptic and physical parameters
const PARAMETER_CATEGORIES = ['ORGANOLEPTIC_PHYSICAL', 'CHEMICAL', 'BACTERIOLOGICAL', 'OTHER'];

// IS 10500:2012 Water Quality Parameters - 13 Parameters as per documentation
// FIELD = Tested on-site by Team Member
// LAB = Tested in laboratory by Admin
// Sorted by category: Organoleptic & Physical → Chemical → Bacteriological
const WATER_QUALITY_PRESETS = [
  // ========== ORGANOLEPTIC & PHYSICAL PARAMETERS ==========
  // 1. Apparent Colour - ENUM (dropdown)
  { code: 'APPARENT_COLOUR', name: 'Apparent Colour', unit: '-', type: 'ENUM', testLocation: 'FIELD', category: 'ORGANOLEPTIC_PHYSICAL', displayOrder: 1, enumOptions: [
    { value: 'Clear', status: 'ACCEPTABLE' },
    { value: 'Yellowish', status: 'PERMISSIBLE' },
    { value: 'Brownish', status: 'NOT_ACCEPTABLE' },
    { value: 'Blackish', status: 'NOT_ACCEPTABLE' }
  ], testMethod: 'Visual' },

  // 2. True Colour - MAX (5 Hazen) - Added just below Apparent Colour
  { code: 'TRUE_COLOUR', name: 'True Colour', unit: 'Hazen', type: 'MAX', testLocation: 'LAB', category: 'ORGANOLEPTIC_PHYSICAL', displayOrder: 2, acceptableMax: 5, permissibleMax: 15, testMethod: 'IS 3025 (Part 4)' },

  // 3. Odour - ENUM (dropdown) - Updated with Agreeable/Non-agreeable
  { code: 'ODOUR', name: 'Odour', unit: '-', type: 'ENUM', testLocation: 'FIELD', category: 'ORGANOLEPTIC_PHYSICAL', displayOrder: 3, enumOptions: [
    { value: 'Agreeable', status: 'ACCEPTABLE' },
    { value: 'Unobjectionable', status: 'ACCEPTABLE' },
    { value: 'Non-agreeable', status: 'NOT_ACCEPTABLE' },
    { value: 'Earthy', status: 'PERMISSIBLE' },
    { value: 'Sewer smell', status: 'NOT_ACCEPTABLE' }
  ], testMethod: 'IS 3025 (Part 5)' },

  // 4. Taste - ENUM (dropdown) - Added after colour/odour as per IS 10500:2012
  { code: 'TASTE', name: 'Taste', unit: '-', type: 'ENUM', testLocation: 'FIELD', category: 'ORGANOLEPTIC_PHYSICAL', displayOrder: 4, enumOptions: [
    { value: 'Agreeable', status: 'ACCEPTABLE' },
    { value: 'Unobjectionable', status: 'ACCEPTABLE' },
    { value: 'Non-agreeable', status: 'NOT_ACCEPTABLE' },
    { value: 'Bitter', status: 'NOT_ACCEPTABLE' },
    { value: 'Salty', status: 'PERMISSIBLE' }
  ], testMethod: 'Sensory' },

  // 5. Temperature - TEXT (free text input)
  { code: 'TEMPERATURE', name: 'Temperature', unit: '°C', type: 'TEXT', testLocation: 'FIELD', category: 'ORGANOLEPTIC_PHYSICAL', displayOrder: 5, testMethod: 'IS 3025 (Part 9)' },

  // 6. pH - RANGE (6.5 to 8.5)
  { code: 'PH', name: 'pH', unit: '-', type: 'RANGE', testLocation: 'FIELD', category: 'ORGANOLEPTIC_PHYSICAL', displayOrder: 6, acceptableMin: 6.5, acceptableMax: 8.5, testMethod: 'IS 3025 (Part 11)' },

  // 7. Turbidity - MAX (1 NTU)
  { code: 'TURBIDITY', name: 'Turbidity', unit: 'NTU', type: 'MAX', testLocation: 'FIELD', category: 'ORGANOLEPTIC_PHYSICAL', displayOrder: 7, acceptableMax: 1, permissibleMax: 5, testMethod: 'IS 3025 (Part 10)' },

  // 8. Total Dissolved Solids - MAX (500 mg/L)
  { code: 'TDS', name: 'Total Dissolved Solids', unit: 'mg/L', type: 'MAX', testLocation: 'LAB', category: 'ORGANOLEPTIC_PHYSICAL', displayOrder: 8, acceptableMax: 500, permissibleMax: 2000, testMethod: 'IS 3025 (Part 16)' },

  // ========== CHEMICAL PARAMETERS ==========
  // 9. Aluminum - MAX (0.03 mg/L)
  { code: 'ALUMINUM', name: 'Aluminum (as Al)', unit: 'mg/L', type: 'MAX', testLocation: 'LAB', category: 'CHEMICAL', displayOrder: 20, acceptableMax: 0.03, permissibleMax: 0.2, testMethod: 'IS 3025 (Part 55)' },

  // 10. Ammonia - MAX (0.5 mg/L)
  { code: 'AMMONIA', name: 'Ammonia (as Total Ammonia-N)', unit: 'mg/L', type: 'MAX', testLocation: 'LAB', category: 'CHEMICAL', displayOrder: 21, acceptableMax: 0.5, permissibleMax: 0.5, testMethod: 'IS 3025 (Part 34)' },

  // 11. Chloride - MAX (250 mg/L)
  { code: 'CHLORIDE', name: 'Chloride (as Cl)', unit: 'mg/L', type: 'MAX', testLocation: 'LAB', category: 'CHEMICAL', displayOrder: 22, acceptableMax: 250, permissibleMax: 1000, testMethod: 'IS 3025 (Part 32)' },

  // 12. Free Residual Chlorine - MAX (0.2 mg/L)
  { code: 'FREE_CHLORINE', name: 'Free Residual Chlorine', unit: 'mg/L', type: 'MAX', testLocation: 'LAB', category: 'CHEMICAL', displayOrder: 23, acceptableMax: 0.2, permissibleMax: 1.0, testMethod: 'IS 3025 (Part 26)' },

  // 13. Total Hardness - MAX (200 mg/L)
  { code: 'HARDNESS', name: 'Total Hardness (as CaCO3)', unit: 'mg/L', type: 'MAX', testLocation: 'LAB', category: 'CHEMICAL', displayOrder: 24, acceptableMax: 200, permissibleMax: 600, testMethod: 'IS 3025 (Part 21)' }
];

const Parameters = () => {
  const toast = useToast();
  const [parameters, setParameters] = useState([]);
  const [allParameterCodes, setAllParameterCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingParam, setEditingParam] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const pagination = usePagination(1, 10);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    unit: '',
    type: 'MAX',
    testLocation: 'FIELD',
    category: 'OTHER',
    displayOrder: 100,
    acceptableMin: '',
    acceptableMax: '',
    permissibleMin: '',
    permissibleMax: '',
    enumOptions: [], // Array of {value, status} for ENUM type
    testMethod: '',
    isMandatory: true
  });

  const fetchParameters = useCallback(async () => {
    try {
      setLoading(true);
      const [paginatedResponse, allResponse] = await Promise.all([
        parametersAPI.getAll(true, pagination.page, pagination.limit),
        parametersAPI.getAllForDropdown()
      ]);
      setParameters(paginatedResponse.data.data);
      // Get all existing codes to filter presets
      setAllParameterCodes(allResponse.data.data.map(p => p.code));
      if (paginatedResponse.data.pagination) {
        pagination.updatePagination(paginatedResponse.data.pagination);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load parameters');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchParameters();
  }, [fetchParameters]);

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      unit: '',
      type: 'MAX',
      testLocation: 'FIELD',
      category: 'OTHER',
      displayOrder: 100,
      acceptableMin: '',
      acceptableMax: '',
      permissibleMin: '',
      permissibleMax: '',
      enumOptions: [],
      testMethod: '',
      isMandatory: true
    });
    setEditingParam(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (param) => {
    setEditingParam(param);
    // Convert enumEvaluation object to array of {value, status}
    let enumOptions = [];
    if (param.enumEvaluation && typeof param.enumEvaluation === 'object') {
      enumOptions = Object.entries(param.enumEvaluation).map(([value, status]) => ({ value, status }));
    }
    setFormData({
      code: param.code || '',
      name: param.name || '',
      unit: param.unit || '',
      type: param.type || 'MAX',
      testLocation: param.testLocation || 'FIELD',
      category: param.category || 'OTHER',
      displayOrder: param.displayOrder ?? 100,
      acceptableMin: param.acceptableLimit?.min ?? '',
      acceptableMax: param.acceptableLimit?.max ?? '',
      permissibleMin: param.permissibleLimit?.min ?? '',
      permissibleMax: param.permissibleLimit?.max ?? '',
      enumOptions: enumOptions,
      testMethod: param.testMethod || '',
      isMandatory: param.isMandatory !== false
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle preset selection
  const handlePresetSelect = (e) => {
    const presetCode = e.target.value;
    if (!presetCode) return;

    const preset = WATER_QUALITY_PRESETS.find(p => p.code === presetCode);
    if (preset) {
      setFormData({
        code: preset.code,
        name: preset.name,
        unit: preset.unit,
        type: preset.type,
        testLocation: preset.testLocation || 'FIELD',
        category: preset.category || 'OTHER',
        displayOrder: preset.displayOrder ?? 100,
        acceptableMin: preset.acceptableMin ?? '',
        acceptableMax: preset.acceptableMax ?? '',
        permissibleMin: preset.permissibleMin ?? '',
        permissibleMax: preset.permissibleMax ?? '',
        enumOptions: preset.enumOptions || [],
        testMethod: preset.testMethod || '',
        isMandatory: preset.isMandatory !== false
      });
    }
  };

  // Count available presets (12 total parameters)
  const availablePresetsCount = WATER_QUALITY_PRESETS.filter(p => !allParameterCodes.includes(p.code)).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        code: formData.code.toUpperCase(),
        name: formData.name,
        unit: formData.unit,
        type: formData.type,
        testLocation: formData.testLocation,
        category: formData.category,
        displayOrder: formData.displayOrder || 100,
        testMethod: formData.testMethod || '',
        isMandatory: formData.isMandatory
      };

      // Set limits based on type
      if (formData.type === 'RANGE') {
        data.acceptableLimit = {
          min: formData.acceptableMin !== '' ? parseFloat(formData.acceptableMin) : null,
          max: formData.acceptableMax !== '' ? parseFloat(formData.acceptableMax) : null
        };
        if (formData.permissibleMin !== '' || formData.permissibleMax !== '') {
          data.permissibleLimit = {
            min: formData.permissibleMin !== '' ? parseFloat(formData.permissibleMin) : null,
            max: formData.permissibleMax !== '' ? parseFloat(formData.permissibleMax) : null
          };
        }
      } else if (formData.type === 'MAX') {
        data.acceptableLimit = {
          min: null,
          max: formData.acceptableMax !== '' ? parseFloat(formData.acceptableMax) : null
        };
        if (formData.permissibleMax !== '') {
          data.permissibleLimit = {
            min: null,
            max: parseFloat(formData.permissibleMax)
          };
        }
      } else if (formData.type === 'ENUM') {
        // Convert enumOptions array to enumEvaluation object {value: status}
        const enumEvaluation = {};
        formData.enumOptions.forEach(opt => {
          if (opt.value && opt.status) {
            enumEvaluation[opt.value] = opt.status;
          }
        });
        data.enumEvaluation = enumEvaluation;
      }

      if (editingParam) {
        await parametersAPI.update(editingParam._id, data);
      } else {
        await parametersAPI.create(data);
      }

      setShowModal(false);
      resetForm();
      fetchParameters();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save parameter');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await parametersAPI.toggle(id);
      fetchParameters();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to toggle parameter');
    }
  };

  const getStandardDisplay = (param) => {
    switch (param.type) {
      case 'RANGE':
        const accMin = param.acceptableLimit?.min;
        const accMax = param.acceptableLimit?.max;
        if (accMin !== null && accMax !== null) {
          return `${accMin} - ${accMax}`;
        }
        return 'N/A';
      case 'MAX':
        const acceptable = param.acceptableLimit?.max;
        const permissible = param.permissibleLimit?.max;
        if (permissible !== null && permissible !== undefined) {
          return `Acceptable: ≤${acceptable}, Permissible: ≤${permissible}`;
        }
        return acceptable !== null ? `≤ ${acceptable}` : 'N/A';
      case 'ENUM':
        // Handle enumEvaluation object
        if (param.enumEvaluation && typeof param.enumEvaluation === 'object') {
          return Object.keys(param.enumEvaluation).join(', ');
        }
        return 'N/A';
      case 'TEXT':
        return 'Text input';
      default:
        return 'N/A';
    }
  };

  // Calculate serial number based on pagination
  const getSerialNumber = (index) => {
    return (pagination.page - 1) * pagination.limit + index + 1;
  };

  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div className="parameters-page">
      <div className="parameters-header">
        <div>
          <h1>Parameter Master</h1>
          <p>Manage water quality parameters (IS 10500:2012 Standards)</p>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          Add Parameter
        </button>
      </div>

      {loading ? (
        <Loading message="Loading parameters..." />
      ) : (
        <>
          <div className="table-container card">
            <table className="table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Code</th>
                  <th>Parameter</th>
                  <th>Test Location</th>
                  <th>Test Method</th>
                  <th>Required</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {parameters.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center' }}>No parameters found</td>
                  </tr>
                ) : (
                  parameters.map((param, index) => (
                    <tr key={param._id} className={!param.isActive ? 'inactive-row' : ''}>
                      <td className="serial-number">{getSerialNumber(index)}</td>
                      <td><code className="param-code">{param.code}</code></td>
                      <td className="param-name">{param.name}</td>
                      <td>
                        <span className={`location-badge location-${param.testLocation?.toLowerCase() || 'field'}`}>
                          {param.testLocation || 'FIELD'}
                        </span>
                      </td>
                      <td className="test-method-cell">{param.testMethod || 'N/A'}</td>
                      <td>
                        <span className={`type-badge ${param.isMandatory !== false ? 'type-max' : 'type-text'}`}>
                          {param.isMandatory !== false ? 'Mandatory' : 'Optional'}
                        </span>
                      </td>
                      <td>
                        <span className={`status-indicator ${param.isActive ? 'active' : 'inactive'}`}>
                          {param.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon"
                            onClick={() => openEditModal(param)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className={`btn-icon ${param.isActive ? 'btn-icon-warning' : 'btn-icon-success'}`}
                            onClick={() => handleToggle(param._id)}
                            title={param.isActive ? 'Disable' : 'Enable'}
                          >
                            {param.isActive ? '🚫' : '✅'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={pagination.goToPage}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
            />
          )}

          <div className="pagination-info">
            Showing {parameters.length} of {pagination.totalItems || parameters.length} parameters
          </div>
        </>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingParam ? 'Edit Parameter' : 'Add Parameter'}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          {/* Preset Selector */}
          {!editingParam && (
            <div className="preset-selector">
              <label className="form-label">
                Select Parameter
                <span className="preset-count">({availablePresetsCount} remaining)</span>
              </label>
              {availablePresetsCount === 0 ? (
                <div className="all-presets-added">
                  All 12 standard parameters have been added!
                </div>
              ) : (
                <select
                  className="form-input preset-dropdown"
                  onChange={handlePresetSelect}
                  defaultValue=""
                >
                  <option value="">-- Select a parameter --</option>
                  {WATER_QUALITY_PRESETS.filter(p => !allParameterCodes.includes(p.code)).map(p => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
              )}
              <small className="form-help">Only shows parameters not yet added to the database</small>
            </div>
          )}

          <div className="form-divider"></div>

          <div className="form-row-modal">
            <div className="form-group">
              <label className="form-label">Code *</label>
              <input
                type="text"
                name="code"
                className="form-input"
                value={formData.code}
                onChange={handleChange}
                required
                placeholder="e.g., PH, TDS, CHLORIDE"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Parameter Name *</label>
              <input
                type="text"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., pH, Total Dissolved Solids"
              />
            </div>
          </div>

          <div className="form-row-modal">
            <div className="form-group">
              <label className="form-label">Unit *</label>
              <input
                type="text"
                name="unit"
                className="form-input"
                value={formData.unit}
                onChange={handleChange}
                required
                placeholder="e.g., mg/L, NTU, -"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Type *</label>
              <select
                name="type"
                className="form-input"
                value={formData.type}
                onChange={handleChange}
                required
              >
                <option value="MAX">Maximum Limit</option>
                <option value="RANGE">Range (Min-Max)</option>
                <option value="ENUM">Options (Enum)</option>
                <option value="TEXT">Text Input</option>
              </select>
            </div>
          </div>

          <div className="form-row-modal">
            <div className="form-group">
              <label className="form-label">Test Location *</label>
              <select
                name="testLocation"
                className="form-input"
                value={formData.testLocation}
                onChange={handleChange}
                required
              >
                <option value="FIELD">FIELD (On-site by Team Member)</option>
                <option value="LAB">LAB (Laboratory by Admin)</option>
              </select>
              <small className="form-help">Where this parameter will be tested</small>
            </div>
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select
                name="category"
                className="form-input"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {PARAMETER_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <small className="form-help">For sorting in reports</small>
            </div>
          </div>

          <div className="form-row-modal">
            <div className="form-group">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                name="displayOrder"
                className="form-input"
                value={formData.displayOrder}
                onChange={handleChange}
                min="1"
                max="999"
              />
              <small className="form-help">Order within category (lower = first)</small>
            </div>
          </div>

          {formData.type === 'RANGE' && (
            <>
              <div className="form-row-modal">
                <div className="form-group">
                  <label className="form-label">Acceptable Min *</label>
                  <input
                    type="number"
                    name="acceptableMin"
                    className="form-input"
                    value={formData.acceptableMin}
                    onChange={handleChange}
                    required
                    step="any"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Acceptable Max *</label>
                  <input
                    type="number"
                    name="acceptableMax"
                    className="form-input"
                    value={formData.acceptableMax}
                    onChange={handleChange}
                    required
                    step="any"
                  />
                </div>
              </div>
              <div className="form-row-modal">
                <div className="form-group">
                  <label className="form-label">Permissible Min (Optional)</label>
                  <input
                    type="number"
                    name="permissibleMin"
                    className="form-input"
                    value={formData.permissibleMin}
                    onChange={handleChange}
                    step="any"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Permissible Max (Optional)</label>
                  <input
                    type="number"
                    name="permissibleMax"
                    className="form-input"
                    value={formData.permissibleMax}
                    onChange={handleChange}
                    step="any"
                  />
                </div>
              </div>
            </>
          )}

          {formData.type === 'MAX' && (
            <div className="form-row-modal">
              <div className="form-group">
                <label className="form-label">Acceptable Limit * (≤ value = ACCEPTABLE)</label>
                <input
                  type="number"
                  name="acceptableMax"
                  className="form-input"
                  value={formData.acceptableMax}
                  onChange={handleChange}
                  required
                  step="any"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Permissible Limit (≤ value = PERMISSIBLE)</label>
                <input
                  type="number"
                  name="permissibleMax"
                  className="form-input"
                  value={formData.permissibleMax}
                  onChange={handleChange}
                  step="any"
                  placeholder="Optional"
                />
              </div>
            </div>
          )}

          {formData.type === 'ENUM' && (
            <div className="form-group">
              <label className="form-label">Enum Options *</label>
              <small className="form-help">Define values and their status evaluation</small>
              <div className="enum-options-list">
                {formData.enumOptions.map((opt, idx) => (
                  <div key={idx} className="enum-option-row">
                    <input
                      type="text"
                      className="form-input"
                      value={opt.value}
                      onChange={(e) => {
                        const newOptions = [...formData.enumOptions];
                        newOptions[idx].value = e.target.value;
                        setFormData({ ...formData, enumOptions: newOptions });
                      }}
                      placeholder="Value (e.g., Clear)"
                    />
                    <select
                      className="form-input"
                      value={opt.status}
                      onChange={(e) => {
                        const newOptions = [...formData.enumOptions];
                        newOptions[idx].status = e.target.value;
                        setFormData({ ...formData, enumOptions: newOptions });
                      }}
                    >
                      <option value="">Status</option>
                      <option value="ACCEPTABLE">ACCEPTABLE</option>
                      <option value="PERMISSIBLE">PERMISSIBLE</option>
                      <option value="NOT_ACCEPTABLE">NOT_ACCEPTABLE</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => {
                        const newOptions = formData.enumOptions.filter((_, i) => i !== idx);
                        setFormData({ ...formData, enumOptions: newOptions });
                      }}
                    >
                      X
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      enumOptions: [...formData.enumOptions, { value: '', status: 'ACCEPTABLE' }]
                    });
                  }}
                >
                  + Add Option
                </button>
              </div>
            </div>
          )}

          <div className="form-row-modal">
            <div className="form-group">
              <label className="form-label">Test Method (IS Standard)</label>
              <input
                type="text"
                name="testMethod"
                className="form-input"
                value={formData.testMethod}
                onChange={handleChange}
                placeholder="e.g., IS 3025 Part 11 (1983)"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Mandatory / Optional</label>
              <select
                name="isMandatory"
                className="form-input"
                value={formData.isMandatory ? 'true' : 'false'}
                onChange={(e) => setFormData({ ...formData, isMandatory: e.target.value === 'true' })}
              >
                <option value="true">Mandatory</option>
                <option value="false">Optional</option>
              </select>
              <small className="form-help">Mandatory parameters must be filled during testing</small>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : (editingParam ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Parameters;
